import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { inrFull, formatDateTime, titleize } from "@/lib/format";
import { CheckCircle2, Circle, AlertCircle, Building2, FileCheck2, ListChecks, Plus, Save } from "lucide-react";
import { toast } from "sonner";

const DOC_ITEMS = [
  { key: "kyc", label: "KYC" }, { key: "income_proof", label: "Income Proof" },
  { key: "bank_statement", label: "Bank Statement" }, { key: "bt_docs", label: "BT Docs" },
  { key: "ownership_proof", label: "Ownership Proof" }, { key: "other_docs", label: "Other Docs" },
];

function Pipeline({ app, stages }) {
  const curIdx = stages.findIndex((s) => s.key === app.status);
  return (
    <div className="space-y-0 py-2">
      {stages.map((s, i) => {
        const done = curIdx >= 0 && i < curIdx;
        const current = s.key === app.status;
        return (
          <div key={s.key} className="flex gap-3">
            <div className="flex flex-col items-center">
              {done ? <CheckCircle2 className="h-6 w-6 text-emerald-500" /> : current ? <div className="h-6 w-6 rounded-full bg-blue-600 flex items-center justify-center"><div className="h-2.5 w-2.5 rounded-full bg-white animate-pulse" /></div> : <Circle className="h-6 w-6 text-slate-300 dark:text-slate-600" />}
              {i < stages.length - 1 && <div className={`w-0.5 flex-1 min-h-[28px] ${done ? "bg-emerald-400" : "bg-slate-200 dark:bg-slate-700"}`} />}
            </div>
            <div className={`pb-5 ${current ? "" : "opacity-80"}`}>
              <div className="flex items-center gap-2">
                <span className={`font-medium text-sm ${current ? "text-blue-700 dark:text-blue-400" : "text-slate-800 dark:text-slate-200"}`}>{s.label}</span>
                {current && <StatusBadge status={app.status} />}
              </div>
              <p className="text-xs text-slate-500 mt-0.5 max-w-md">{s.desc}</p>
              {s.required_docs?.length > 0 && <p className="text-[11px] text-slate-400 mt-1">Docs: {s.required_docs.join(", ")}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function Documents({ clientAppId }) {
  const qc = useQueryClient();
  const { data: summary, isLoading } = useQuery({
    queryKey: ["doc-checklist", clientAppId],
    queryFn: async () => (await api.get(`/applications/${clientAppId}/doc-checklist/summary`)).data,
    enabled: !!clientAppId,
  });
  const mutate = useMutation({
    mutationFn: async ({ doc_key, status }) => (await api.patch(`/applications/${clientAppId}/doc-checklist`, { doc_key, status })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["doc-checklist", clientAppId] }); toast.success("Checklist updated"); },
    onError: (e) => toast.error(apiError(e)),
  });

  if (!clientAppId) return <p className="text-sm text-slate-400 py-4">No client application linked.</p>;
  if (isLoading) return <p className="text-sm text-slate-400 py-4">Loading checklist…</p>;

  const received = new Set(summary?.received_keys || []);
  const query = new Set(summary?.query_keys || []);

  return (
    <div className="py-2 space-y-2">
      <div className="flex items-center gap-2 text-sm mb-3">
        {summary?.all_required_received ? <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> All required documents received</span>
          : <span className="text-amber-600 flex items-center gap-1"><AlertCircle className="h-4 w-4" /> Missing: {(summary?.missing_keys || []).map((k) => titleize(k)).join(", ") || "—"}</span>}
      </div>
      {DOC_ITEMS.map((d) => {
        const state = received.has(d.key) ? "received" : query.has(d.key) ? "query" : "pending";
        return (
          <div key={d.key} className="flex items-center justify-between border border-slate-100 dark:border-slate-800 rounded-lg px-3 py-2" data-testid={`doc-item-${d.key}`}>
            <div className="flex items-center gap-2">
              {state === "received" ? <FileCheck2 className="h-4 w-4 text-emerald-500" /> : state === "query" ? <AlertCircle className="h-4 w-4 text-amber-500" /> : <Circle className="h-4 w-4 text-slate-300" />}
              <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{d.label}</span>
              {state !== "pending" && <span className={`text-xs ${state === "received" ? "text-emerald-600" : "text-amber-600"}`}>· {state}</span>}
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant={state === "received" ? "default" : "outline"} className={state === "received" ? "bg-emerald-600 hover:bg-emerald-700 h-7 text-white" : "h-7"} onClick={() => mutate.mutate({ doc_key: d.key, status: "received" })} data-testid={`doc-received-${d.key}`}>Received</Button>
              <Button size="sm" variant={state === "query" ? "default" : "outline"} className={state === "query" ? "bg-amber-500 hover:bg-amber-600 h-7 text-white" : "h-7"} onClick={() => mutate.mutate({ doc_key: d.key, status: "query" })} data-testid={`doc-query-${d.key}`}>Query</Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BankTab({ app }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canEdit = ["Admin", "Manager", "Team Leader", "Coordinator"].includes(user?.role);
  const [feedback, setFeedback] = useState(app.latest_feedback || "");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ bank_code: "", loan_amount: "", feedback: "", note: "" });
  const { data: lenders = [] } = useQuery({ queryKey: ["lenders"], queryFn: async () => (await api.get("/lenders")).data });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["applications"] }); qc.invalidateQueries({ queryKey: ["dashboard-stats"] }); };
  const updateFb = useMutation({
    mutationFn: async () => (await api.patch(`/applications/${app.id}/bank-submissions/${app.bank_submission_id}`, { feedback })).data,
    onSuccess: () => { invalidate(); toast.success("Feedback saved"); },
    onError: (e) => toast.error(apiError(e)),
  });
  const addSub = useMutation({
    mutationFn: async () => (await api.post(`/applications/${app.id}/bank-submissions`, { bank_code: form.bank_code, loan_amount: form.loan_amount ? Number(form.loan_amount) : null, feedback: form.feedback || null, note: form.note || null })).data,
    onSuccess: () => { invalidate(); setAdding(false); setForm({ bank_code: "", loan_amount: "", feedback: "", note: "" }); toast.success("Bank submission added"); },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <div className="py-2 space-y-4">
      <div className="space-y-3">
        {[["Bank", app.bank_name], ["Reference No.", app.reference_number], ["Loan Amount", inrFull(app.loan_amount)], ["Coordinator", app.coordinator_name], ["Status", app.status], ["Updated", formatDateTime(app.updated_at)]].map(([k, v]) => (
          <div key={k} className="flex justify-between border-b border-slate-100 dark:border-slate-800 pb-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{k}</span>
            <span className="text-sm text-slate-800 dark:text-slate-200 font-num text-right">{v || "—"}</span>
          </div>
        ))}
      </div>

      {canEdit && app.bank_submission_id && (
        <div className="space-y-2">
          <Label>Bank Feedback</Label>
          <Textarea value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="Enter latest bank feedback…" data-testid="bank-feedback-input" />
          <Button size="sm" className="bg-blue-700 hover:bg-blue-800 text-white" disabled={updateFb.isPending} onClick={() => updateFb.mutate()} data-testid="bank-feedback-save"><Save className="h-4 w-4 mr-1" />Save Feedback</Button>
        </div>
      )}

      {canEdit && (
        <div className="border-t border-slate-100 dark:border-slate-800 pt-3">
          {!adding ? (
            <Button variant="outline" size="sm" onClick={() => setAdding(true)} data-testid="add-bank-submission-button"><Plus className="h-4 w-4 mr-1" />Add Bank Submission</Button>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Bank *</Label>
                  <Select value={form.bank_code} onValueChange={(v) => setForm({ ...form, bank_code: v })}><SelectTrigger data-testid="bank-submission-bank-select"><SelectValue placeholder="Select bank" /></SelectTrigger><SelectContent>{lenders.map((l) => <SelectItem key={l.id || l.code} value={l.code}>{l.name}</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="space-y-1.5"><Label>Loan Amount</Label><Input type="number" value={form.loan_amount} onChange={(e) => setForm({ ...form, loan_amount: e.target.value })} data-testid="bank-submission-amount-input" /></div>
              </div>
              <div className="space-y-1.5"><Label>Feedback</Label><Textarea value={form.feedback} onChange={(e) => setForm({ ...form, feedback: e.target.value })} data-testid="bank-submission-feedback-input" /></div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setAdding(false)}>Cancel</Button>
                <Button size="sm" className="bg-blue-700 hover:bg-blue-800 text-white" disabled={!form.bank_code || addSub.isPending} onClick={() => addSub.mutate()} data-testid="bank-submission-save">Add Submission</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function ApplicationDetail({ app, open, onOpenChange }) {
  const { data: stagesData } = useQuery({ queryKey: ["pipeline-stages"], queryFn: async () => (await api.get("/applications/pipeline-stages")).data, enabled: open });
  const stages = stagesData?.stages || [];
  if (!app) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white dark:bg-slate-900" data-testid="application-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{app.applicant_name || app.lead_name}</DialogTitle>
          <DialogDescription className="flex items-center gap-2 flex-wrap">
            <span>{app.bank_name}</span>·<span>{app.loan_type}</span>·<span className="font-num">{inrFull(app.loan_amount)}</span>
            {app.reference_number && <span className="font-num text-xs">Ref {app.reference_number}</span>}
          </DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="pipeline">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="pipeline" data-testid="tab-pipeline"><ListChecks className="h-4 w-4 mr-1.5" />Pipeline</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents"><FileCheck2 className="h-4 w-4 mr-1.5" />Documents</TabsTrigger>
            <TabsTrigger value="bank" data-testid="tab-bank"><Building2 className="h-4 w-4 mr-1.5" />Bank</TabsTrigger>
          </TabsList>
          <ScrollArea className="max-h-[55vh] mt-3 pr-3">
            <TabsContent value="pipeline"><Pipeline app={app} stages={stages} /></TabsContent>
            <TabsContent value="documents"><Documents clientAppId={app.client_application_id} /></TabsContent>
            <TabsContent value="bank"><BankTab app={app} /></TabsContent>
          </ScrollArea>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
